import { PayrollMetaData } from './types';

/**
 * Rows the skip/resume checkboxes may act on.
 *
 * A skipped employee is always resumable: the monolith finalize stamps
 * isFinal = true on skipped rows too, so filtering on !isFinal alone made a
 * fully-skipped finalized month a dead end — no checkbox, and (post-#11233)
 * no Make Changes either, because the finalized bucket excludes skipped rows
 * (ISS-3678912). Paid rows are never selectable, and a finalized non-skipped
 * row still is not: resume must never de-finalize an employee who was not
 * skipped.
 *
 * Money safety does not live here: resuming puts the row back in PENDING,
 * which breaks the Execute identity (T === F + S + E) in PayrollActions, so
 * Execute stays hidden until the month is re-finalized with fresh amounts.
 */
export const getSkipResumeSelectableMeta = (payrollMeta: PayrollMetaData[]): PayrollMetaData[] =>
  payrollMeta.filter((meta) => !meta.isBlocked && (!meta.isFinal || meta.isCancelled));

/**
 * Whether the grid offers bulk skip/resume selection at all.
 *
 * The isExecutionRequested guard exists because skipped rows are selectable
 * even in fully-finalized months now: without it the merchant could resume an
 * employee while an execution request is pending or in flight and watch the
 * row silently join the next cycle. The backend enforces the same guard
 * (RunPayrollLib::abortIfExecutionIsRequested); this keeps the UI honest for
 * data loaded before the execution request.
 */
/**
 * Whether a finalized-skipped row gets the per-row edit pencil (which routes to
 * the ResumePayroll modal for skipped rows).
 *
 * The bulk checkbox is plan-gated (Pro / runPayrollBulkSkip / rearch-M2), so for
 * every other org this pencil is the ONLY merchant-facing way to resume a
 * finalized-skipped employee (ISS-3678912). Deferred new joiners keep the Final
 * pill — onEdit would route them to EditSalary, and they are not skipped. The
 * remaining guards mirror the existing fall-through pencil exactly.
 */
export const isRowResumeCtaVisible = (
  entry: {
    isFinal?: boolean;
    isCancelled?: boolean;
    isBlocked?: boolean;
    newJoinerArrearPaymentMonth?: string | null;
  },
  flags: {
    hasEditPermission: boolean;
    isPayrollBeingFinalized: boolean;
    isEditCtaVisible: boolean;
    isNewJoinerArrearFeatureEnabled: boolean;
  },
): boolean =>
  !!entry.isFinal &&
  !!entry.isCancelled &&
  !entry.isBlocked &&
  !(flags.isNewJoinerArrearFeatureEnabled && !!entry.newJoinerArrearPaymentMonth) &&
  flags.hasEditPermission &&
  !flags.isPayrollBeingFinalized &&
  flags.isEditCtaVisible;

export const isBulkSelectionAllowed = ({
  selectableCount,
  isBulkActionsEnabled,
  isChangeRequestPendingOnFinalize,
  isExecutionRequested,
}: {
  selectableCount: number;
  isBulkActionsEnabled: boolean;
  isChangeRequestPendingOnFinalize: boolean;
  isExecutionRequested: boolean;
}): boolean =>
  selectableCount > 0 &&
  isBulkActionsEnabled &&
  !isChangeRequestPendingOnFinalize &&
  !isExecutionRequested;
